/**
 * AIAnalystPanel — Reusable embedded panel that displays a single module's
 * AI analyst report with executive summary, status pill, structured sections,
 * a follow-up chat, per-item editing, and version history / restore.
 *
 *   <AIAnalystPanel projectId={projectId} moduleKey="MACRO_SCHEDULE" />
 */
import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, RefreshCw, AlertCircle,
  Target, Lightbulb, ListChecks, GitBranch, MessageCircle, Send, Copy,
  ChevronDown, ChevronUp, Bot, User, Zap, ShieldAlert,
  History, Trash2, Plus, RotateCcw,
} from 'lucide-react';

const STATUS_PILL = {
  GREEN: { bg: 'rgba(16,185,129,0.15)', fg: '#10b981', label: 'GREEN' },
  AMBER: { bg: 'rgba(245,158,11,0.15)', fg: '#d97706', label: 'AMBER' },
  RED:   { bg: 'rgba(239,68,68,0.15)',  fg: '#dc2626', label: 'RED' },
};
const SEV_COLOR = { CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#ca8a04', LOW: '#10b981' };

export default function AIAnalystPanel({ projectId, moduleKey, defaultOpen = true, compact = false }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(defaultOpen);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const chatEndRef = useRef(null);

  // ── Item selection (for edit-via-chat) — supports single OR multi-select
  //    Single: { section, index } for legacy single-select flows
  //    Multi:  Set<`${section}::${index}`> when multi mode is on
  const [selected, setSelected] = useState(null);
  const [multiMode, setMultiMode] = useState(false);
  const [multiSelected, setMultiSelected] = useState(() => new Set());
  const toggleMulti = (section, index) => {
    const key = `${section}::${index}`;
    setMultiSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const clearMulti = () => setMultiSelected(new Set());
  // Versions panel
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [diff, setDiff] = useState(null);  // { versionNo, sections: [{section, archivedCount, currentCount, delta}] }

  const SECTIONS_TO_DIFF = ['insights', 'critical_activities', 'gaps',
                            'catch_up_plan', 'schedule_optimization', 'best_practice_gaps'];

  const loadDiff = async (versionNo) => {
    try {
      const r = await api.get(`/v1/projects/${projectId}/ai-analyst/${moduleKey}/versions/${versionNo}`);
      const archived = r.data?.report || {};
      const current  = report?.report || {};
      // Best-effort key extractor for each item (id, title, etc.)
      const keyOf = (item) => {
        if (!item || typeof item !== 'object') return String(item);
        return item.id || item.title || item.name || item.action || item.lever
             || item.area || item.check || JSON.stringify(item).slice(0, 60);
      };
      const sections = SECTIONS_TO_DIFF
        .map(s => {
          const a = Array.isArray(archived[s]) ? archived[s] : [];
          const c = Array.isArray(current[s]) ? current[s] : [];
          const aKeys = a.map(keyOf);
          const cKeys = c.map(keyOf);
          const added   = c.filter((it, i) => !aKeys.includes(cKeys[i]));
          const removed = a.filter((it, i) => !cKeys.includes(aKeys[i]));
          return { section: s, archivedCount: a.length, currentCount: c.length,
                   delta: c.length - a.length, added, removed };
        })
        .filter(x => x.archivedCount > 0 || x.currentCount > 0);
      setDiff({ versionNo, sections });
    } catch (e) {
      console.warn('diff load failed', e);
    }
  };

  const loadLatest = async () => {
    if (!projectId || !moduleKey) return;
    try {
      const r = await api.get(`/v1/projects/${projectId}/ai-analyst/${moduleKey}/latest`);
      setReport(r.data);
    } catch (e) {
      if (e?.response?.status !== 404) console.warn('Latest fetch failed', e);
      setReport(null);
    }
  };
  const loadVersions = async () => {
    if (!projectId || !moduleKey) return;
    try {
      const r = await api.get(`/v1/projects/${projectId}/ai-analyst/${moduleKey}/versions`);
      setVersions(r.data?.versions || []);
    } catch (e) {
      console.warn('versions load failed', e);
      setVersions([]);
    }
  };
  useEffect(() => { loadLatest(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [projectId, moduleKey]);

  // ── Item-level edit helpers ─────────────────────────────────────
  const applyEdit = async ({ operation, section, itemIndex, changes, newItem, reason }) => {
    try {
      const r = await api.post(`/v1/projects/${projectId}/ai-analyst/${moduleKey}/edit`,
        { operation, section, itemIndex, changes, newItem, reason: reason || '' });
      // Update local state with the returned report so UI reflects immediately
      setReport(prev => prev ? { ...prev, report: r.data.report, editedAt: new Date().toISOString() } : prev);
      toast.success(`${operation.toUpperCase()} · ${section.replace(/_/g, ' ')}`);
      setSelected(null);
      return r.data;
    } catch (e) {
      toast.error(`Edit failed: ${e?.response?.data?.detail || e.message}`);
      throw e;
    }
  };

  const deleteItem = (section, index) => {
    if (!window.confirm(`Delete item ${index + 1} from ${section.replace(/_/g, ' ')}?`)) return;
    applyEdit({ operation: 'remove', section, itemIndex: index,
                reason: `User deleted item ${index}` });
  };

  const dispatchItem = async (section, index, target = 'RAID_RISK') => {
    try {
      const r = await api.post(
        `/v1/projects/${projectId}/ai-analyst/${moduleKey}/dispatch`,
        { section, itemIndex: index, target });
      toast.success(`Sent to RAID · ${r.data.raidNumber}`);
    } catch (e) {
      toast.error(`Dispatch failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const bulkDispatch = async (target = 'RAID_RISK') => {
    const items = Array.from(multiSelected).map(k => {
      const [section, idxStr] = k.split('::');
      return { section, itemIndex: parseInt(idxStr, 10) };
    });
    if (items.length === 0) return toast.info('Select items first (long-press or click while Multi is on)');
    if (!window.confirm(`Dispatch ${items.length} selected items to RAID as ${target.replace('RAID_', '')}?`)) return;
    let ok = 0;
    // Dispatch in descending index order per section so removals don't shift indices
    const grouped = {};
    items.forEach(({ section, itemIndex }) => (grouped[section] = grouped[section] || []).push(itemIndex));
    for (const [section, idxs] of Object.entries(grouped)) {
      for (const idx of idxs.sort((a, b) => b - a)) {
        try {
          await api.post(`/v1/projects/${projectId}/ai-analyst/${moduleKey}/dispatch`,
            { section, itemIndex: idx, target });
          ok++;
        } catch (e) { console.warn('bulk dispatch fail', e); }
      }
    }
    toast.success(`Dispatched ${ok} / ${items.length} to RAID`);
    clearMulti();
    setMultiMode(false);
  };

  const restoreVersion = async (versionNo) => {
    if (!window.confirm(`Restore report to version ${versionNo}? Current state will be snapshotted first.`)) return;
    try {
      const r = await api.post(`/v1/projects/${projectId}/ai-analyst/${moduleKey}/restore/${versionNo}`);
      setReport(r.data);
      setShowVersions(false);
      loadVersions();
      toast.success(`Restored to version ${versionNo}`);
    } catch (e) {
      toast.error(`Restore failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const run = async () => {
    if (!projectId || !moduleKey) return;
    setLoading(true);
    try {
      // Kick off background job. Backend returns {jobId, status:'pending'}
      // for fresh runs, or the cached report directly when status==='done'.
      const r = await api.post(`/v1/projects/${projectId}/ai-analyst/${moduleKey}/analyze?force=true`, {}, { timeout: 30000 });
      if (r.data?.status === 'done' && r.data?.report) {
        setReport(r.data);
        toast.success(`${r.data.moduleLabel || moduleKey} analyst report ready`);
        return;
      }
      const jobId = r.data?.jobId;
      if (!jobId) {
        // Server returned an unexpected shape (e.g. legacy synchronous response)
        setReport(r.data);
        toast.success('Analyst report ready');
        return;
      }
      // Poll the job status until done or failed (max ~3 min)
      const POLL_MS = 2500;
      const MAX_TRIES = 80;
      for (let attempt = 0; attempt < MAX_TRIES; attempt++) {
        await new Promise(res => setTimeout(res, POLL_MS));
        try {
          const s = await api.get(`/v1/projects/${projectId}/ai-analyst/${moduleKey}/status/${jobId}`, { timeout: 15000 });
          if (s.data?.status === 'done' && s.data?.report) {
            setReport(s.data.report);
            toast.success(`${s.data.report.moduleLabel || moduleKey} analyst report ready`);
            return;
          }
          if (s.data?.status === 'failed') {
            toast.error(`Analyst failed: ${s.data.error || 'unknown error'}`);
            return;
          }
        } catch (pollErr) {
          // Transient poll failures shouldn't kill the run — keep retrying.
          console.warn('poll error', pollErr);
        }
      }
      toast.error('Analyst is taking longer than expected — please try again.');
    } catch (e) {
      toast.error(`Analyst failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  };

  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || chatBusy) return;
    setChatBusy(true);
    setChatMsgs(prev => [...prev, { role: 'user', content: msg }]);
    setChatInput('');
    try {
      // Detect edit intent via simple keyword heuristics on the CLIENT first
      // — cheap, deterministic, matches "delete/remove item N", "add ...", "change severity of item N to HIGH".
      const editIntent = parseEditIntent(msg, selected);
      if (editIntent) {
        // Emit an immediate assistant echo describing the action
        setChatMsgs(prev => [...prev, {
          role: 'assistant',
          content: `Applying: **${editIntent.operation.toUpperCase()}** on ${editIntent.section.replace(/_/g, ' ')}` +
                   (editIntent.itemIndex != null ? ` (item ${editIntent.itemIndex + 1})` : ''),
        }]);
        await applyEdit({ ...editIntent, reason: `Chat: "${msg}"` });
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        return;
      }
      // Otherwise → normal grounded Q&A
      const r = await api.post(`/v1/projects/${projectId}/ai-analyst/${moduleKey}/chat`,
        { message: selected ? `[Selected: ${selected.section} · item ${selected.index + 1}] ${msg}` : msg,
          history: chatMsgs.slice(-6) },
        { timeout: 60000 });
      setChatMsgs(prev => [...prev, { role: 'assistant', content: r.data.reply || '(no reply)' }]);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) {
      setChatMsgs(prev => [...prev, { role: 'assistant', content: `[Error] ${e?.response?.data?.detail || e.message}` }]);
    } finally { setChatBusy(false); }
  };

  const copyAll = () => {
    if (!report) return;
    navigator.clipboard.writeText(JSON.stringify(report.report, null, 2));
    toast.success('Copied report JSON');
  };

  const rep = report?.report || {};
  const perfKey = report?.domain === 'cost' ? 'cost_performance' : 'schedule_performance';
  const perf = rep[perfKey] || {};
  const pill = STATUS_PILL[perf.status] || STATUS_PILL.AMBER;

  return (
    <div className="rounded-xl overflow-hidden" data-testid={`ai-analyst-panel-${moduleKey}`}
         style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      {/* HEADER */}
      <div className="flex items-center gap-2 px-3 py-2"
           style={{ background: 'var(--surface-bg)', borderBottom: '1px solid var(--border-subtle)' }}>
        <Sparkles className="w-4 h-4" style={{ color: report?.iconColor || 'var(--accent)' }} />
        <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
          AI Analyst{report?.moduleLabel ? ` — ${report.moduleLabel}` : ''}
        </div>
        {perf.status && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold ml-1"
                style={{ background: pill.bg, color: pill.fg }}
                data-testid={`ai-analyst-status-${moduleKey}`}>
            {pill.label}
          </span>
        )}
        <span className="ml-auto text-[10px] hidden md:inline" style={{ color: 'var(--text-muted)' }}>
          {report?.generatedAt ? `Updated ${new Date(report.generatedAt).toLocaleString()}` : 'No analysis yet'}
        </span>
        <button onClick={() => { setShowVersions(v => !v); if (!showVersions) loadVersions(); }}
                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] hover:opacity-80"
                style={{ background: 'var(--surface-bg)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}
                data-testid={`ai-analyst-versions-btn-${moduleKey}`}
                title="Version history">
          <History className="w-3 h-3" /> Versions
        </button>
        <button onClick={() => { setMultiMode(m => !m); if (multiMode) clearMulti(); }}
                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] hover:opacity-80"
                style={{
                  background: multiMode ? 'rgba(59,130,246,0.2)' : 'var(--surface-bg)',
                  color: multiMode ? '#3b82f6' : 'var(--text-secondary)',
                  border: '1px solid var(--border-subtle)'
                }}
                data-testid={`ai-analyst-multi-btn-${moduleKey}`}
                title="Multi-select mode">
          <Zap className="w-3 h-3" /> {multiMode ? `Multi (${multiSelected.size})` : 'Multi'}
        </button>
        {multiMode && multiSelected.size > 0 && (
          <button onClick={() => bulkDispatch('RAID_RISK')}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold hover:opacity-80"
                  style={{ background: '#3b82f6', color: 'white' }}
                  data-testid={`ai-analyst-bulk-dispatch-${moduleKey}`}>
            → RAID ({multiSelected.size})
          </button>
        )}
        <button onClick={run} disabled={loading}
                className="flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold bg-[#1E4E8C] hover:bg-[#2E70AD] text-white transition-colors disabled:opacity-60"
                style={{ opacity: loading ? 0.6 : 1 }}
                data-testid={`ai-analyst-run-${moduleKey}`}>
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          {report ? 'Re-run' : 'Run analysis'}
        </button>
        <button onClick={() => setOpen(o => !o)} className="p-1 rounded"
                style={{ color: 'var(--text-muted)' }}
                aria-label={open ? 'Collapse' : 'Expand'}>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {open && (
        <div className="p-3 space-y-3 text-[12px]" style={{ color: 'var(--text-primary)' }}>

          {/* Versions history panel */}
          {showVersions && (
            <div className="rounded-lg p-2"
                 style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }}
                 data-testid={`ai-analyst-versions-${moduleKey}`}>
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>
                <History className="w-3 h-3" /> Version history ({versions.length}) — hover a row to compare
              </div>
              {versions.length === 0 ? (
                <div className="text-[11px] italic py-2" style={{ color: 'var(--text-muted)' }}>
                  No history yet — edits and re-runs will snapshot the previous report here.
                </div>
              ) : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {versions.map((v) => (
                    <div key={v.versionNo} className="rounded hover:bg-slate-500/5"
                         onMouseEnter={() => loadDiff(v.versionNo)}
                         onMouseLeave={() => setDiff(null)}>
                      <div className="flex items-center gap-2 text-[11px] p-1.5">
                        <span className="font-mono w-8 flex-none text-slate-500">v{v.versionNo}</span>
                        <span className="flex-1 truncate" style={{ color: 'var(--text-secondary)' }}>{v.reason}</span>
                        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                          {v.snapshotAt ? new Date(v.snapshotAt).toLocaleString() : '—'}
                        </span>
                        <button onClick={() => restoreVersion(v.versionNo)}
                                className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] hover:opacity-80"
                                style={{ background: 'rgba(59,130,246,0.15)', color: '#3b82f6' }}
                                data-testid={`ai-analyst-restore-${v.versionNo}`}
                                title="Restore this version">
                          <RotateCcw className="w-3 h-3" /> Restore
                        </button>
                      </div>
                      {/* Inline diff — item-level */}
                      {diff && diff.versionNo === v.versionNo && (
                        <div className="border-t border-slate-500/20 px-2 py-2 space-y-2 text-[10px]">
                          {diff.sections.map((s) => (
                            <div key={s.section} className="rounded p-1.5"
                                 style={{ background: 'rgba(148,163,184,0.08)' }}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="uppercase tracking-wider font-bold" style={{ color: 'var(--text-secondary)' }}>
                                  {s.section.replace(/_/g, ' ')}
                                </span>
                                <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>
                                  v{v.versionNo}: {s.archivedCount} → now: {s.currentCount}
                                </span>
                                {s.delta !== 0 && (
                                  <span className="text-[9px] px-1 rounded font-bold"
                                        style={{ background: (s.delta > 0 ? '#3b82f6' : '#dc2626') + '22',
                                                 color: s.delta > 0 ? '#3b82f6' : '#dc2626' }}>
                                    {s.delta > 0 ? '+' : ''}{s.delta}
                                  </span>
                                )}
                              </div>
                              {s.added?.length > 0 && (
                                <div className="space-y-0.5 mb-1">
                                  {s.added.slice(0, 3).map((it, k) => (
                                    <div key={`a-${k}`} className="pl-1 border-l-2 border-emerald-500 truncate"
                                         style={{ color: '#10b981' }}>
                                      + {it.title || it.name || it.action || it.lever || it.area || JSON.stringify(it).slice(0, 60)}
                                    </div>
                                  ))}
                                  {s.added.length > 3 && <div style={{ color: 'var(--text-muted)' }}>+ {s.added.length - 3} more</div>}
                                </div>
                              )}
                              {s.removed?.length > 0 && (
                                <div className="space-y-0.5">
                                  {s.removed.slice(0, 3).map((it, k) => (
                                    <div key={`r-${k}`} className="pl-1 border-l-2 border-rose-500 truncate"
                                         style={{ color: '#dc2626' }}>
                                      – {it.title || it.name || it.action || it.lever || it.area || JSON.stringify(it).slice(0, 60)}
                                    </div>
                                  ))}
                                  {s.removed.length > 3 && <div style={{ color: 'var(--text-muted)' }}>– {s.removed.length - 3} more</div>}
                                </div>
                              )}
                              {(s.added?.length === 0 && s.removed?.length === 0) && (
                                <div style={{ color: 'var(--text-muted)' }} className="italic">No item changes</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {!report ? (
            <div className="py-8 text-center" style={{ color: 'var(--text-muted)' }}>
              <Bot className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--text-disabled)' }} />
              <div className="text-sm">No analysis yet. Click <b>Run analysis</b> to generate.</div>
              <div className="text-[10px] mt-1" style={{ color: 'var(--text-disabled)' }}>Powered by Cognara · cached 1 hour</div>
            </div>
          ) : (
            <>
          {/* Slippage Ribbon — top 3 signals so users can't miss them */}
          {(() => {
            const topSignals = [];
            (rep.critical_activities || []).slice(0, 2).forEach(c => topSignals.push({
              kind: 'CRIT', sev: c.severity, text: `${c.id || ''} ${c.name || ''}`.trim() || c.impact,
            }));
            (rep.insights || []).slice(0, 3 - topSignals.length).forEach(i => topSignals.push({
              kind: 'INSIGHT', sev: 'INFO', text: i.title,
            }));
            if (topSignals.length === 0) return null;
            return (
              <div className="rounded-lg p-2 flex items-center gap-2 flex-wrap"
                   style={{ background: pill.bg, border: `1px solid ${pill.fg}55` }}
                   data-testid={`ai-analyst-ribbon-${moduleKey}`}>
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold flex-none"
                      style={{ background: pill.fg + '33', color: pill.fg }}>TOP SIGNALS</span>
                {topSignals.map((s, i) => (
                  <span key={i} className="flex items-center gap-1 text-[11px] font-medium truncate max-w-[420px]"
                        style={{ color: 'var(--text-primary)' }}>
                    <span className="px-1 py-0.5 rounded text-[8px] font-bold"
                          style={{ background: (SEV_COLOR[s.sev] || '#64748b') + '22',
                                   color: SEV_COLOR[s.sev] || 'var(--text-muted)' }}>
                      {s.kind === 'CRIT' ? s.sev || 'CRIT' : 'INSIGHT'}
                    </span>
                    <span className="truncate">{s.text}</span>
                  </span>
                ))}
              </div>
            );
          })()}

              <div className="rounded-lg p-3"
                   style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }}
                   data-testid={`ai-analyst-summary-${moduleKey}`}>
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                  <Target className="w-3 h-3" /> Executive Summary
                </div>
                <div className="text-[12px] leading-relaxed" style={{ color: 'var(--text-primary)' }}>{rep.executive_summary || '—'}</div>
              </div>

              {/* Performance block */}
              {perf?.narrative && (
                <div className="rounded-lg p-3"
                     style={{ background: 'var(--surface-bg)', border: `1px solid ${pill.fg}55` }}
                     data-testid={`ai-analyst-perf-${moduleKey}`}>
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                    {report.domain === 'cost' ? 'Cost' : 'Schedule'} Performance · {pill.label}
                  </div>
                  {perf.headline_metric && (
                    <div className="text-base font-bold mb-1" style={{ color: pill.fg }}>{perf.headline_metric}</div>
                  )}
                  <div className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{perf.narrative}</div>
                </div>
              )}

              {/* Insights */}
              {Array.isArray(rep.insights) && rep.insights.length > 0 && (
                <Section title="Insights" icon={Lightbulb} testid={`ai-analyst-insights-${moduleKey}`}>
                  {rep.insights.map((i, idx) => (
                    <Editable key={idx} section="insights" index={idx} selected={selected}
                              onSelect={setSelected} onDelete={deleteItem} onDispatch={dispatchItem} multiMode={multiMode} multiSelected={multiSelected} onToggleMulti={toggleMulti} target="RAID_RISK">
                      <div className="rounded p-2"
                           style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }}>
                        <div className="font-semibold text-[11px]" style={{ color: 'var(--text-primary)' }}>{i.title}</div>
                        <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>{i.detail}</div>
                        {i.evidence && <div className="text-[10px] font-mono mt-1" style={{ color: 'var(--text-muted)' }}>→ {i.evidence}</div>}
                      </div>
                    </Editable>
                  ))}
                </Section>
              )}

              {/* Critical Activities */}
              {Array.isArray(rep.critical_activities) && rep.critical_activities.length > 0 && (
                <Section title="Critical Activities" icon={AlertCircle} testid={`ai-analyst-critical-${moduleKey}`}>
                  {rep.critical_activities.map((c, idx) => (
                    <Editable key={idx} section="critical_activities" index={idx} selected={selected}
                              onSelect={setSelected} onDelete={deleteItem} onDispatch={dispatchItem} multiMode={multiMode} multiSelected={multiSelected} onToggleMulti={toggleMulti} target="RAID_RISK">
                      <div className="rounded p-2"
                           style={{ background: 'var(--surface-bg)', border: `1px solid ${(SEV_COLOR[c.severity] || 'var(--border-subtle)')}44` }}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold"
                                style={{ background: (SEV_COLOR[c.severity] || '#9ca3af') + '22', color: SEV_COLOR[c.severity] || 'var(--text-muted)' }}>
                            {c.severity}
                          </span>
                          <span className="font-mono text-[11px]" style={{ color: 'var(--text-primary)' }}>{c.id}</span>
                          <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{c.name}</span>
                        </div>
                        <div className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>{c.impact}</div>
                      </div>
                    </Editable>
                  ))}
                </Section>
              )}

              {/* Gaps */}
              {Array.isArray(rep.gaps) && rep.gaps.length > 0 && (
                <Section title="Gaps vs Plan" icon={GitBranch} testid={`ai-analyst-gaps-${moduleKey}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {rep.gaps.map((g, idx) => (
                      <Editable key={idx} section="gaps" index={idx} selected={selected}
                                onSelect={setSelected} onDelete={deleteItem} onDispatch={dispatchItem} multiMode={multiMode} multiSelected={multiSelected} onToggleMulti={toggleMulti} target="RAID_ISSUE">
                        <div className="rounded p-2"
                             style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }}>
                          <div className="font-semibold text-[11px]" style={{ color: 'var(--text-primary)' }}>{g.area}</div>
                          <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                            Current: <span style={{ color: '#d97706' }}>{g.current}</span> ·
                            Target: <span style={{ color: '#10b981' }}>{g.target}</span>
                            {g.deltaPct != null && <span className="ml-1">(Δ {g.deltaPct}%)</span>}
                          </div>
                        </div>
                      </Editable>
                    ))}
                  </div>
                </Section>
              )}

              {/* Catch-up Plan */}
              {Array.isArray(rep.catch_up_plan) && rep.catch_up_plan.length > 0 && (
                <Section title="Catch-up Plan" icon={ListChecks} testid={`ai-analyst-catchup-${moduleKey}`}>
                  {rep.catch_up_plan.map((p, idx) => (
                    <Editable key={idx} section="catch_up_plan" index={idx} selected={selected}
                              onSelect={setSelected} onDelete={deleteItem} onDispatch={dispatchItem} multiMode={multiMode} multiSelected={multiSelected} onToggleMulti={toggleMulti} target="RAID_ACTION">
                      <div className="rounded p-2 flex items-start gap-2"
                           style={{ background: 'var(--surface-bg)', border: '1px solid rgba(16,185,129,0.3)' }}>
                        <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5"
                             style={{ background: 'rgba(16,185,129,0.2)', color: '#10b981' }}>{idx + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[11px]" style={{ color: 'var(--text-primary)' }}>{p.action}</div>
                          <div className="text-[10px] mt-0.5 flex flex-wrap gap-2" style={{ color: 'var(--text-muted)' }}>
                            <span><b>Owner:</b> {p.owner}</span>
                            <span><b>By:</b> {p.by_when}</span>
                            {p.expected_recovery && <span style={{ color: '#10b981' }}>→ {p.expected_recovery}</span>}
                          </div>
                        </div>
                      </div>
                    </Editable>
                  ))}
                </Section>
              )}

              {/* Schedule Optimization — verb-first levers the planner can pull this week */}
              {Array.isArray(rep.schedule_optimization) && rep.schedule_optimization.length > 0 && (
                <Section title="Schedule Optimization" icon={Zap} testid={`ai-analyst-optimization-${moduleKey}`}>
                  {rep.schedule_optimization.map((o, idx) => {
                    const effortColor = o.effort === 'LOW' ? '#10b981' : o.effort === 'HIGH' ? '#dc2626' : '#d97706';
                    return (
                      <div key={idx} className="rounded p-2"
                           style={{ background: 'var(--surface-bg)', border: '1px solid rgba(59,130,246,0.3)' }}>
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                               style={{ background: 'rgba(59,130,246,0.2)' }}>
                            <Zap className="w-3 h-3" style={{ color: '#3b82f6' }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-[11px]" style={{ color: 'var(--text-primary)' }}>{o.lever}</div>
                            {o.rationale && (
                              <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>{o.rationale}</div>
                            )}
                            <div className="text-[10px] mt-1 flex flex-wrap gap-2 items-center" style={{ color: 'var(--text-muted)' }}>
                              {o.expected_gain && (
                                <span style={{ color: '#10b981' }}><b>Gain:</b> {o.expected_gain}</span>
                              )}
                              {o.effort && (
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold"
                                      style={{ background: effortColor + '22', color: effortColor }}>
                                  EFFORT · {o.effort}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </Section>
              )}

              {/* Best-Practice Gaps — DCMA-14 / AACE 39R-06 / GAO compliance audit */}
              {Array.isArray(rep.best_practice_gaps) && rep.best_practice_gaps.length > 0 && (
                <Section title="Best-Practice Gaps · DCMA-14 / AACE" icon={ShieldAlert} testid={`ai-analyst-bpgaps-${moduleKey}`}>
                  {rep.best_practice_gaps.map((g, idx) => (
                    <div key={idx} className="rounded p-2"
                         style={{ background: 'var(--surface-bg)', border: `1px solid ${(SEV_COLOR[g.severity] || 'var(--border-subtle)')}44` }}>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold"
                              style={{ background: (SEV_COLOR[g.severity] || '#9ca3af') + '22', color: SEV_COLOR[g.severity] || 'var(--text-muted)' }}>
                          {g.severity || 'INFO'}
                        </span>
                        {g.standard && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-mono"
                                style={{ background: 'var(--surface-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)' }}>
                            {g.standard}
                          </span>
                        )}
                        <span className="font-semibold text-[11px]" style={{ color: 'var(--text-primary)' }}>{g.check}</span>
                      </div>
                      <div className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                        Observed: <span style={{ color: '#d97706' }}>{g.observed}</span>
                        {g.threshold && (<> · Threshold: <span style={{ color: '#10b981' }}>{g.threshold}</span></>)}
                      </div>
                      {g.remediation && (
                        <div className="text-[11px] mt-1.5 px-2 py-1 rounded"
                             style={{ background: 'var(--surface-elevated)', color: 'var(--text-secondary)', borderLeft: '2px solid #3b82f6' }}>
                          <b>Fix:</b> {g.remediation}
                        </div>
                      )}
                    </div>
                  ))}
                </Section>
              )}

              {/* Footer */}
              <div className="flex items-center gap-2 pt-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                <button onClick={copyAll} className="flex items-center gap-1 px-2 py-1 rounded hover:opacity-80"
                        style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
                        data-testid={`ai-analyst-copy-${moduleKey}`}>
                  <Copy className="w-3 h-3" /> Copy JSON
                </button>
                <span className="ml-auto">Powered by Cognara</span>
              </div>

              {/* Chat */}
              {!compact && (
                <div className="rounded-lg" style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }}>
                  <div className="flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-wider"
                       style={{ borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>
                    <MessageCircle className="w-3 h-3" /> Follow-up Q&amp;A · Edit items via chat
                    {selected && (
                      <span className="ml-auto normal-case tracking-normal text-[10px] px-1.5 py-0.5 rounded"
                            style={{ background: 'rgba(59,130,246,0.15)', color: '#3b82f6' }}>
                        Selected: {selected.section.replace(/_/g, ' ')} · item {selected.index + 1}
                        <button onClick={() => setSelected(null)}
                                className="ml-1 opacity-60 hover:opacity-100"
                                data-testid="ai-clear-selection">✕</button>
                      </span>
                    )}
                  </div>
                  <div className="px-3 py-2 space-y-2 max-h-64 overflow-y-auto" data-testid={`ai-analyst-chat-${moduleKey}`}>
                    {chatMsgs.length === 0 && (
                      <div className="text-[11px] italic space-y-1" style={{ color: 'var(--text-muted)' }}>
                        <div>Ask follow-ups or issue edit commands. Try:</div>
                        <div><i>&quot;delete item 3 from insights&quot;</i> · <i>&quot;add insight about pot-lining risk&quot;</i></div>
                        <div><i>&quot;change severity of item 2 to HIGH&quot;</i> · <i>&quot;why is SPI dropping?&quot;</i></div>
                        <div className="text-[10px] pt-1" style={{ color: 'var(--text-disabled)' }}>Tip: click an item on the left to select it, then say &quot;delete this&quot;.</div>
                      </div>
                    )}
                    {chatMsgs.map((m, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                             style={{ background: m.role === 'user' ? 'rgba(59,130,246,0.15)' : 'rgba(6,182,212,0.15)' }}>
                          {m.role === 'user'
                            ? <User className="w-3 h-3" style={{ color: '#3b82f6' }} />
                            : <Bot  className="w-3 h-3" style={{ color: '#06b6d4' }} />}
                        </div>
                        <div className="text-[11px] whitespace-pre-wrap flex-1" style={{ color: 'var(--text-primary)' }}>{m.content}</div>
                      </div>
                    ))}
                    {chatBusy && <div className="text-[11px] italic" style={{ color: 'var(--text-muted)' }}><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Thinking…</div>}
                    <div ref={chatEndRef} />
                  </div>
                  <div className="flex items-center gap-1 px-2 py-2" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <input value={chatInput}
                           onChange={e => setChatInput(e.target.value)}
                           onKeyDown={e => { if (e.key === 'Enter') sendChat(); }}
                           placeholder={selected ? `Edit selected — try "delete this" or "change severity to HIGH"` : `Ask, or type "add / delete / change ..."`}
                           className="flex-1 px-2 py-1 rounded text-[11px]"
                           style={{ background: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                           data-testid={`ai-analyst-chat-input-${moduleKey}`} />
                    <button onClick={sendChat} disabled={chatBusy || !chatInput.trim()}
                            className="p-1.5 rounded"
                            style={{ background: chatBusy || !chatInput.trim() ? 'var(--surface-elevated)' : 'var(--accent)',
                                     color: chatBusy || !chatInput.trim() ? 'var(--text-muted)' : 'var(--text-inverse)' }}
                            data-testid={`ai-analyst-chat-send-${moduleKey}`}>
                      <Send className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
        <Icon className="w-3 h-3" /> {title}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Editable item wrapper — adds hover-visible delete + Send-to-RAID + click-to-select.
// ─────────────────────────────────────────────────────────────
function Editable({ section, index, selected, onSelect, onDelete, onDispatch, children, target = 'RAID_RISK',
                    multiMode = false, multiSelected, onToggleMulti }) {
  const isSel = selected && selected.section === section && selected.index === index;
  const isMultiSel = multiMode && multiSelected && multiSelected.has(`${section}::${index}`);
  return (
    <div className="relative group"
         onClick={() => { multiMode ? onToggleMulti(section, index) : onSelect({ section, index }); }}
         data-testid={`ai-item-${section}-${index}`}
         style={{
           outline: isSel || isMultiSel ? '2px solid #3b82f6' : 'none',
           outlineOffset: 1,
           borderRadius: 6,
           cursor: 'pointer',
           background: isMultiSel ? 'rgba(59,130,246,0.06)' : undefined,
         }}>
      {multiMode && (
        <div className="absolute top-1 left-1 flex-none">
          <input type="checkbox" readOnly checked={isMultiSel}
                 className="w-3 h-3 rounded pointer-events-none"
                 data-testid={`ai-item-multi-${section}-${index}`} />
        </div>
      )}
      <div style={{ paddingLeft: multiMode ? 18 : 0 }}>{children}</div>
      <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onDispatch(section, index, target); }}
          className="p-1 rounded bg-blue-500/10 hover:bg-blue-500/25 text-blue-500"
          title={`Send to RAID as ${target.replace('RAID_', '')}`}
          data-testid={`ai-item-dispatch-${section}-${index}`}>
          <Zap className="w-3 h-3" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(section, index); }}
          className="p-1 rounded bg-red-500/10 hover:bg-red-500/25 text-red-500"
          title="Delete this item"
          data-testid={`ai-item-delete-${section}-${index}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      {isSel && !multiMode && (
        <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-4 rounded-full bg-blue-500" />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Client-side edit-intent parser — keeps roundtrips down for simple cases.
// Understands: "delete/remove item N", "delete this / selected",
//              "add {section} <text>", "change severity of item N to HIGH".
// Returns null → let the Cognara LLM answer as a normal Q&A.
// ─────────────────────────────────────────────────────────────
const SECTION_ALIASES = {
  insight: 'insights', insights: 'insights',
  risk: 'critical_activities', risks: 'critical_activities',
  'critical activity': 'critical_activities', 'critical activities': 'critical_activities',
  gap: 'gaps', gaps: 'gaps',
  'catch-up': 'catch_up_plan', 'catch up': 'catch_up_plan', catchup: 'catch_up_plan',
  optimization: 'schedule_optimization', optimisation: 'schedule_optimization',
  lever: 'schedule_optimization', levers: 'schedule_optimization',
  'best practice': 'best_practice_gaps',
  recommendation: 'recommendations', recommendations: 'recommendations',
  action: 'catch_up_plan', actions: 'catch_up_plan',
};

function parseEditIntent(raw, selected) {
  const msg = raw.toLowerCase().trim();

  // "delete/remove selected" / "delete this"
  if ((/(delete|remove|drop)\s+(the\s+)?(selected|this)/.test(msg)) && selected) {
    return { operation: 'remove', section: selected.section, itemIndex: selected.index };
  }

  // "delete/remove item N" (optionally "from <section>")
  const rm = msg.match(/(?:delete|remove|drop)\s+(?:item\s+)?(\d+)(?:\s+(?:from|in)\s+([a-z\- ]+))?/);
  if (rm) {
    const idx = parseInt(rm[1], 10) - 1;
    let section = selected?.section;
    if (rm[2]) {
      const key = Object.keys(SECTION_ALIASES).find(k => rm[2].includes(k));
      if (key) section = SECTION_ALIASES[key];
    }
    if (section != null) return { operation: 'remove', section, itemIndex: idx };
  }

  // "change severity of item N to HIGH"
  const chg = msg.match(/(?:change|set|update)\s+(severity|owner|action|title|status)\s+of\s+(?:item\s+)?(\d+)\s+to\s+(.+)/);
  if (chg) {
    const field = chg[1];
    const idx   = parseInt(chg[2], 10) - 1;
    const value = chg[3].trim().replace(/["'.\s]+$/g, '');
    const section = selected?.section;
    if (section != null) {
      return {
        operation: 'update', section, itemIndex: idx,
        changes: { [field]: /^(critical|high|medium|low|open|closed|active)$/i.test(value) ? value.toUpperCase() : value },
      };
    }
  }

  // "add insight about ..."/"add risk ..."/"add action ..."
  const add = msg.match(/(?:add|append|create)\s+(?:a\s+)?(?:new\s+)?([a-z\- ]+?)(?:\s+about|\s*:)\s+(.+)/);
  if (add) {
    const key = Object.keys(SECTION_ALIASES).find(k => add[1].includes(k));
    if (key) {
      const section = SECTION_ALIASES[key];
      const text = add[2].trim();
      const item = section === 'insights' ? { title: text.slice(0, 60), detail: text }
        : section === 'critical_activities' ? { severity: 'MEDIUM', id: `USER-${Date.now().toString(36).slice(-4).toUpperCase()}`, name: text, impact: text }
        : section === 'catch_up_plan' ? { action: text, owner: 'PMO', by_when: 'TBD' }
        : section === 'schedule_optimization' ? { lever: text.slice(0, 60), rationale: text, effort: 'MEDIUM' }
        : section === 'gaps' ? { area: text.slice(0, 60), current: '—', target: '—' }
        : { title: text };
      return { operation: 'add', section, newItem: item };
    }
  }
  return null;
}

